"use client";

import { useState } from "react";
import { Globe, Copy } from "lucide-react";
import GeneratorPage from "../GeneratorPage";
import { CATEGORIES } from "@/constants/categories";
import { Button, Input, Select } from "@/components/design-system";
import { generateIPs, IP_VERSIONS, IP_TYPES, GeneratedIP, IPVersion } from "@/lib/ipAddressGenerator";
import { useTranslations, useLocale } from "next-intl";
import { Controller } from "react-hook-form";
import SchemaScript from "@/components/common/SchemaScript";
import { Routes } from "@/constants/routes";
import { Locale } from "@/constants/locales";

export default function IPAddressGenerator() {
    const t = useTranslations("ip-addresses");
    const locale = useLocale() as Locale;
    const [isGenerating, setIsGenerating] = useState(false);
    const [generated, setGenerated] = useState<(GeneratedIP & { id: string })[]>([]);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const [fallbackForm] = useState({
        version: "ipv4" as IPVersion,
        type: "any",
        count: 10,
    });

    const handleGenerate = (e: React.FormEvent, formData?: any) => {
        e.preventDefault();
        const data = formData || fallbackForm;
        const count = Number(data.count);

        setIsGenerating(true);

        setTimeout(() => {
            const results = generateIPs({
                count,
                version: data.version as IPVersion,
                type: data.type,
            });

            const resultsWithId = results.map(ip => ({
                id: Math.random().toString(36).substr(2, 9),
                ...ip
            }));

            setGenerated(resultsWithId);
            setIsGenerating(false);
        }, 400);
    };

    const handleCopy = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    const handleCopyAll = () => {
        const allText = generated.map(g => g.ip).join("\n");
        navigator.clipboard.writeText(allText);
        setCopiedId("all");
        setTimeout(() => setCopiedId(null), 1500);
    };

    const customResultsRenderer = (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t("results.title")}</h3>
                {generated.length > 0 && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleCopyAll}
                    >
                        {copiedId === "all" ? t("results.copyAllSuccess") : t("results.copyAll")}
                    </Button>
                )}
            </div>
            <div className="grid gap-4 md:grid-cols-1">
                {generated.length === 0 ? (
                    <div className="bg-muted/30 border border-dashed border-muted-foreground/25 rounded-lg p-8 text-center min-h-[120px] flex flex-col items-center justify-center">
                        <Globe className="h-8 w-8 text-muted-foreground/50 mb-3" />
                        <span className="text-muted-foreground">{t("results.noResults")}</span>
                    </div>
                ) : (
                    generated.map((ip) => (
                        <div key={ip.id} className="relative bg-white dark:bg-slate-900 rounded-xl border border-border p-5 shadow-sm hover:shadow-md transition-all group">
                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleCopy(ip.ip, ip.id)}
                                    className="p-2 text-muted-foreground hover:text-primary bg-background border rounded-md shadow-sm"
                                    title={t("form.copyButton")}
                                >
                                    {copiedId === ip.id ? <span className="text-green-500 font-bold text-xs">{t("form.copiedButton")}</span> : <Copy className="h-4 w-4" />}
                                </button>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="mt-1 p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg text-emerald-600 dark:text-emerald-400">
                                    <Globe className="h-5 w-5" />
                                </div>
                                <div className="space-y-1 flex-1">
                                    <div className="flex gap-2">
                                        <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                                            {ip.version}
                                        </span>
                                        <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">
                                            {ip.type}
                                        </span>
                                    </div>
                                    <div className="font-mono text-lg text-slate-900 dark:text-white break-all">{ip.ip}</div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    const formConfig = {
        values: fallbackForm,
        fields: [
            {
                key: "version",
                label: t("form.version"),
                type: "select" as const,
                validation: { required: true },
            },
            {
                key: "type",
                label: t("form.type"),
                type: "select" as const,
                validation: { required: true },
            },
            {
                key: "count",
                label: t("form.count"),
                type: "number" as const,
                validation: { required: true, min: 1, max: 50 },
            },
        ],
        onChange: () => { },
    };

    return (
        <>
            <GeneratorPage
                title={t("title")}
                description={t("description")}
                icon={<Globe className="h-4 w-4" />}
                category={CATEGORIES.personal}
                generatedData={generated.length ? generated.map(g => g.ip).join("\n") : null}
                isGenerating={isGenerating}
                onGenerate={handleGenerate}
                formConfig={formConfig}
                resultsRenderer={customResultsRenderer}
                faqs={t.raw("faqs")}
                similarGenerators={[]}
            >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block mb-1 font-medium">{t("form.version")}</label>
                        <Controller
                            name="version"
                            render={({ field }) => (
                                <Select
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.target.value)}
                                >
                                    {IP_VERSIONS.map(v => (
                                        <option key={v.code} value={v.code}>{v.name}</option>
                                    ))}
                                </Select>
                            )}
                        />
                    </div>
                    <div>
                        <label className="block mb-1 font-medium">{t("form.type")}</label>
                        <Controller
                            name="type"
                            render={({ field }) => (
                                <Select
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.target.value)}
                                >
                                    {IP_TYPES.map(t => (
                                        <option key={t.code} value={t.code}>{t.name}</option>
                                    ))}
                                </Select>
                            )}
                        />
                    </div>
                    <div>
                        <label className="block mb-1 font-medium">{t("form.count")}</label>
                        <Controller
                            name="count"
                            rules={{ required: true, min: 1, max: 50 }}
                            render={({ field }) => (
                                <Input
                                    type="number"
                                    min={1}
                                    max={50}
                                    value={field.value}
                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                    placeholder={t("form.countPlaceholder")}
                                />
                            )}
                        />
                    </div>
                </div>
            </GeneratorPage>

            <SchemaScript route={Routes['ip-addresses']} locale={locale} faqItems={t.raw("faqs")} />
        </>
    );
}
